import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

// The target counts as reached once its top is above this share of the screen height.
const REACHED_AT = 0.8;

/**
 * A floating button that scrolls to the block it sits in.
 * It shows while that block is still below the screen and hides once the shopper reaches or passes it.
 *
 * @extends {Component}
 */
class JumpButtonComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Reading the position on scroll, rather than with an IntersectionObserver, also catches jumps
    // that skip over the target, such as going straight back to the top of the page.
    window.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #scheduleUpdate = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    const target = this.parentElement;
    if (!target) return;

    const { top } = target.getBoundingClientRect();
    this.toggleAttribute('data-visible', top > window.innerHeight * REACHED_AT);
  }

  /**
   * Scrolls to the target and moves focus there so keyboard and screen reader users land on it too.
   */
  scrollToTarget() {
    const target = this.parentElement;
    if (!target) return;

    target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }
}

if (!customElements.get('jump-button-component')) {
  customElements.define('jump-button-component', JumpButtonComponent);
}
